/**
 * @coeldery/cloudinary-upload —— **Cloudinary 圖片上載**共用模組
 *
 * 為何係模組（R1／R3）：生態內已經有**兩份**同款實作（`85AI` `POST /api/admin/cloudinary-sign`
 * ＋ `coeldery-family-tree` `POST /api/cloudinary-sign`＋`src/utils/cloudinaryUpload.ts`），
 * 兩者**同一帳號、同一簽名規則**，只係欄位命名／端點路徑唔同 → 抽成一個共用模組（本檔為第一份）。
 *
 * 契約（跟 85AI，即 Cloudinary 官方 signed upload 欄位嘅 snake_case）：
 *   ① 前端 `POST {signUrl}`（body 可帶 `{ folder }`）→ 回
 *      `{ ok:true, signature, timestamp, api_key, cloud_name, folder }`（**永不含 api_secret**）
 *   ② 前端 `POST https://api.cloudinary.com/v1_1/{cloud_name}/image/upload`
 *      FormData：`file` / `api_key` / `timestamp` / `signature` / `folder`（**冇 preset**）
 *   ③ 回 `{ secure_url }`（本模組只回 `url`）
 *
 * ⚠️ 邊界：**唔**讀 env、**唔**寫 D1、**唔**識產品；`apiSecret` 只由呼叫者（server 端）注入。
 * ⚠️ 網絡錯誤**照 throw**（唔准當簽名／上載失敗）—— 跟 family-tree `decisions/004` 嘅定論。
 */

/** 上載大小上限（5MB；同 family-tree／85AI 一致） */
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

/** 簽名端點回應（只含公開值） */
export interface CloudinarySignResponse {
  ok: boolean;
  error?: string;
  signature: string;
  timestamp: number;
  api_key: string;
  cloud_name: string;
  folder: string;
}

export interface SignParamsInput {
  /** API secret（**只准 server 端**；唔可以送到前端） */
  apiSecret: string;
  cloudName: string;
  apiKey: string;
  folder: string;
  /** 測試可注入固定時間（Unix 秒） */
  nowSeconds?: number;
}

/**
 * 產生 Cloudinary signed upload 參數（**server 端用**）。
 * 簽名規則：`SHA-1( "folder=<folder>&timestamp=<ts>" + apiSecret )`（key 字母序，尾接 secret 唔加 `&`）
 * ⚠️ 用 Web Crypto（Workers／瀏覽器都有），零 npm 依賴。
 */
export async function signUploadParams(input: SignParamsInput): Promise<CloudinarySignResponse> {
  const timestamp = Math.floor(input.nowSeconds ?? Date.now() / 1000);
  const signature = await sha1Hex(`folder=${input.folder}&timestamp=${timestamp}${input.apiSecret}`);
  return {
    ok: true,
    signature,
    timestamp,
    api_key: input.apiKey,
    cloud_name: input.cloudName,
    folder: input.folder,
  };
}

/** SHA-1 → lowercase hex（Workers／瀏覽器通用） */
export async function sha1Hex(message: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(message));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export type UploadFailure = 'sign' | 'upload' | 'config';

export type UploadResult = { ok: true; url: string } | { ok: false; reason: UploadFailure; message?: string };

export interface UploadInput {
  file: File | Blob;
  /** 簽名端點（例 `/api/cloudinary-sign`） */
  signUrl: string;
  /** 資料夾（呼叫者可指定；server 端一般會用白名單過濾） */
  folder?: string;
  /** 測試注入用 */
  fetchImpl?: typeof fetch;
}

/**
 * 上載一張圖去 Cloudinary（**前端用**）：攞簽名 → 直上 Cloudinary → 回 `secure_url`。
 *
 * 失敗分類（**唔准扮成功**）：
 *  · `config` → 伺服器未設定 Cloudinary（例 503）
 *  · `sign`   → 攞唔到簽名（回應唔 ok／欄位唔齊）
 *  · `upload` → Cloudinary 拒收（HTTP 非 2xx／冇 `secure_url`）
 * ⚠️ **網絡／fetch throw 一律照 throw**（由呼叫者外層 catch）—— 唔准歸類為「簽名失敗」。
 */
export async function uploadToCloudinary(input: UploadInput): Promise<UploadResult> {
  const doFetch = input.fetchImpl ?? fetch;

  const res = await doFetch(input.signUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    /*
     * ⚠️ **唔加** `credentials: 'include'` —— ① Workers 嘅 `RequestInit` 型別冇呢個欄位；
     *    ② 簽名端點本身**唔睇 cookie**（family-tree `decisions/004` 已查證）→ 加咗都冇作用。
     */
    body: JSON.stringify(input.folder ? { folder: input.folder } : {}),
  });
  const sig = (await res.json().catch(() => null)) as CloudinarySignResponse | null;
  if (!sig || !sig.ok) {
    const reason: UploadFailure = res.status === 503 || res.status === 500 ? 'config' : 'sign';
    return { ok: false, reason, message: sig?.error };
  }
  if (!sig.signature || !sig.timestamp || !sig.api_key || !sig.cloud_name || !sig.folder) {
    return { ok: false, reason: 'sign', message: '簽名回應唔完整' };
  }

  const fd = new FormData();
  fd.append('file', input.file);
  fd.append('api_key', sig.api_key);
  fd.append('timestamp', String(sig.timestamp));
  fd.append('signature', sig.signature);
  fd.append('folder', sig.folder);

  const up = await doFetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, {
    method: 'POST',
    body: fd,
  });
  const out = (await up.json().catch(() => null)) as { secure_url?: string; error?: { message?: string } } | null;
  if (!up.ok || !out?.secure_url) {
    return { ok: false, reason: 'upload', message: out?.error?.message };
  }
  return { ok: true, url: out.secure_url };
}
